/**
 * Tenant Context Provider (Contract v4.0.0, §8.3)
 *
 * Provides the active tenant_id to all components via React Context.
 * Reads the authenticated user's tenant memberships from AuthContext
 * and defaults to the first membership. Supports tenant switching for
 * users with multiple memberships.
 */

'use client';

import { createContext, useContext, useState, useMemo, useRef, useCallback, ReactNode } from 'react';
import { useAuth } from './auth-context';
import { getAuthClient } from './auth-client';
import type { TenantMembership, UserRole } from '@/types/gcp';
import { logger } from './logger';
import { consumeFreshLogin } from './login-tenant-handoff';

// Empty string sentinel — if no authenticated tenant is resolved, the
// backend will reject requests as 400/401 rather than silently serving
// data scoped to a hardcoded tenant. NEVER default to a real tenant UUID.
const EMPTY_TENANT_ID = '';

// Persists the user's active tenant selection across hard refreshes.
// Without this, selectedTenantId resets to null on reload, activeTenant
// falls back to tenants[0] (the user's default tenant), and useCaseDetail
// fires with the wrong tenant_id for cases owned by a non-default tenant
// — backend returns 403/404 and the case-detail layout bounces the user
// to /dashboard with an "Access Denied to Case" toast. Scoped per
// user_id so multiple accounts on one device don't share selection.
const TENANT_STORAGE_KEY_PREFIX = 'webapp.activeTenantId';

function tenantStorageKey(userId: string | undefined | null): string | null {
  if (!userId) return null;
  return `${TENANT_STORAGE_KEY_PREFIX}.${userId}`;
}

function readPersistedTenantId(userId: string | undefined | null): string | null {
  if (typeof window === 'undefined') return null;
  const key = tenantStorageKey(userId);
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writePersistedTenantId(
  userId: string | undefined | null,
  tenantId: string | null,
): void {
  if (typeof window === 'undefined') return;
  const key = tenantStorageKey(userId);
  if (!key) return;
  try {
    if (tenantId) {
      window.localStorage.setItem(key, tenantId);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Quota / private-mode failures are non-fatal — user loses
    // cross-reload persistence but can still switch tenants in-session.
  }
}

// Exported (review fix edv-2.5 M1) so the Storybook stub at
// `.storybook/mocks/tenant-context.tsx` can pin its `useTenant` return
// type to the canonical shape — any future field added here will then
// fail the Storybook build instead of silently drifting away from the
// real surface.
export interface TenantContextType {
  tenantId: string;
  tenantName: string;
  tenants: TenantMembership[];
  switchTenant: (tenantId: string) => void;
  // Active-tenant authorization (issues #557 / #565). These reflect the role
  // the user holds in the tenant they are CURRENTLY acting in — not
  // `user.role`, which is only the primary/first-listed membership. Admin UI
  // gating MUST source admin status from here (or the tenant-aware
  // `usePermissions`), never from `useAuth().isTenantAdmin` (primary-only).
  // `isSuperAdmin` is system-wide and tenant-independent.
  role: UserRole | null;
  isTenantAdmin: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const tenants: TenantMembership[] = useMemo(
    () => user?.tenants ?? [],
    [user?.tenants]
  );

  // Hydrate / reset selection when the authenticated user changes
  // (initial load, login, switch user). Reads localStorage and accepts
  // the persisted value only if it still maps to an active membership;
  // otherwise clears stale storage and falls through to the default
  // (first membership) tenant. Done synchronously during render — the
  // canonical "derive state from props" pattern — so useCaseDetail is
  // gated by enabled:!!tenantId until the correct tenant resolves and
  // never fires a request against the default tenant for a case owned
  // by a different one.
  const prevUserIdRef = useRef<string | undefined>(undefined);
  if (prevUserIdRef.current !== user?.user_id) {
    prevUserIdRef.current = user?.user_id;

    let matchedTenant: TenantMembership | undefined;
    let authClientTenantId: string | null = null;
    const authClient = getAuthClient() as ReturnType<typeof getAuthClient> & {
      getTenant?: () => string | null;
    };
    if (typeof authClient.getTenant === 'function') {
      const currentGcipTenantId = authClient.getTenant();
      authClientTenantId = currentGcipTenantId;
      if (currentGcipTenantId) {
        // `gcip_tenant_id` is a real field on TenantMembership since gh #872.
        // Until then the server never sent it, so this match could not succeed
        // and login always fell through to tenants[0] — the most recently
        // granted tenant. That is why #1165's client-side fix (PR #1166) had
        // no observable effect.
        matchedTenant = tenants.find(
          (t) => t.gcip_tenant_id === currentGcipTenantId
        );
      }
    }

    // Precedence, in order (gh #872 / #1165):
    //   1. the agency just signed into, on the load that FOLLOWS a sign-in
    //   2. a still-valid persisted choice (an in-app switch survives refresh)
    //   3. the agency signed into, on any later load with nothing persisted
    //   4. nothing — fall through to the tenants[0] default
    //
    // 1 and 2 are the two tickets pulling opposite ways: a login must beat a
    // stale localStorage from an earlier session, while an in-app switch must
    // beat the login tenant on refresh. consumeFreshLogin() is what separates
    // them, and it is one-shot, so the login tenant wins exactly once.
    const freshLoginGcipTenantId = consumeFreshLogin();
    const persisted = user?.user_id ? readPersistedTenantId(user.user_id) : null;
    const isPersistedValid =
      persisted !== null && tenants.some((t) => t.tenant_id === persisted);
    const isFreshLogin =
      matchedTenant !== undefined &&
      freshLoginGcipTenantId !== null &&
      freshLoginGcipTenantId === authClientTenantId;

    if (matchedTenant && (isFreshLogin || !isPersistedValid)) {
      if (selectedTenantId !== matchedTenant.tenant_id) {
        setSelectedTenantId(matchedTenant.tenant_id);
      }
      if (user?.user_id) {
        writePersistedTenantId(user.user_id, matchedTenant.tenant_id);
      }
    } else if (isPersistedValid) {
      if (selectedTenantId !== persisted) setSelectedTenantId(persisted);
    } else {
      if (user?.user_id && persisted) writePersistedTenantId(user.user_id, null);
      if (selectedTenantId !== null) setSelectedTenantId(null);
    }
  }

  const activeTenant = useMemo(() => {
    if (selectedTenantId) {
      const match = tenants.find(t => t.tenant_id === selectedTenantId);
      if (match) return match;
    }
    if (tenants.length > 0) return tenants[0];
    return null;
  }, [selectedTenantId, tenants]);

  const resolvedTenantId = activeTenant?.tenant_id ?? user?.tenant_id ?? EMPTY_TENANT_ID;
  if (!resolvedTenantId && user) {
    // User is authenticated but carries no tenant membership — surface this
    // as a warning so we catch misconfigured tenants in dev instead of
    // silently routing to a fallback.
    logger.warn('[TenantProvider] Authenticated user has no resolvable tenant_id', {
      userId: user.user_id,
      email: user.email,
    });
  }

  const switchTenant = useCallback(
    (tenantId: string) => {
      setSelectedTenantId(tenantId);
      writePersistedTenantId(user?.user_id, tenantId);
    },
    [user?.user_id],
  );

  // Role for the tenant the user is actively viewing. Falls back to the
  // primary-membership role only when no active membership resolves (e.g. a
  // super_admin with a single bootstrap grant). super_admin elevation is
  // tenant-independent and read from the auth profile's system_role.
  const activeRole: UserRole | null = activeTenant?.role ?? user?.role ?? null;
  const isSuperAdmin = user?.system_role === 'super_admin';
  const isTenantAdmin = activeRole === 'admin';
  const isAdmin = isTenantAdmin || isSuperAdmin;

  const value: TenantContextType = {
    tenantId: resolvedTenantId,
    tenantName:
      activeTenant?.tenant_name ??
      (user?.tenant_id ? 'Current Organization' : ''),
    tenants,
    switchTenant,
    role: activeRole,
    isTenantAdmin,
    isSuperAdmin,
    isAdmin,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error('useTenant must be used within TenantProvider');
  }

  return context;
}
